import React, { useState } from 'react';

import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Table, { TableActionBar, TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import {
  displayHc,
  displayThrowRate,
  displayTime,
  getBreaks,
  isBreakQuarter,
  isFeatureFlagEnabled
} from '../../../utils';
import { chainWalk } from '../../../../../utils/helpers';
import { useRoleSpeedMapping } from '../../../../../utils/roleSpeedUtils';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import { BUSINESS_VIEW_NAME, DEFAULT_SPEED, ALL_SPEEDS, SSD_FEATURE_FLAGS_LIST } from '../../../../../utils/constants';
export const SINGLES_ROLES = ['singlespick', 'singlespack'];

const TimeWindowHeader = ({ quarters, index, quarter, shift, timeline, speed }) => {
  const text =
    index === quarters.length - 1
      ? `${displayTime(quarter)} - ${displayTime(shift.end)}`
      : `${displayTime(quarter)} - ${displayTime(quarters[index + 1])}`;
  return (
    <Column>
      <Text type="h200">
        {speed === DEFAULT_SPEED && isBreakQuarter(quarter, getBreaks(timeline)) ? 'Break' : ''}&nbsp;
      </Text>
      <Text type="h200">{text}</Text>
    </Column>
  );
};

const filterHeadcountBySpeed = (headcountAllocation, speed, roleSpeedMapping) => {
  return headcountAllocation.filter((headcountAllocationItem) => {
    // Get the speed from the role mapping using process_role
    const roleSpeed = roleSpeedMapping[headcountAllocationItem.process_role] || DEFAULT_SPEED;

    if (roleSpeed === speed) {
      return true;
    }

    if (speed === DEFAULT_SPEED && roleSpeed === DEFAULT_SPEED) {
      return true;
    }

    return false;
  });
};

const filterRolesBySpeed = (roles, speed) => {
  return roles.filter((role) => {
    if (role.role && role.role.speed && role.role.speed === speed) {
      return true;
    }

    if (speed === DEFAULT_SPEED && (!role.role.speed || role.role.speed === DEFAULT_SPEED)) {
      return true;
    }

    return false;
  });
};

const DepartmentTable = ({
  roles,
  headcountAllocation,
  department,
  shift,
  timeline,
  singlesEnabled,
  business,
  speed
}) => {
  const roleSpeedMapping = useRoleSpeedMapping(roles);
  const speedFilteredHeadcount = filterHeadcountBySpeed(headcountAllocation, speed, roleSpeedMapping);
  const speedFilteredRoles = filterRolesBySpeed(roles, speed);

  const roleList = speedFilteredRoles.filter((r) => {
    // Always filter by department
    const isSameDepartment = r.role.departmentName === department.name;

    // Optionally apply Singles logic
    if (SINGLES_ROLES.includes(r.role.name) && business === BUSINESS_VIEW_NAME.CORE) {
      return isSameDepartment && singlesEnabled;
    }

    // Filter by business
    if (business === r.role.businessType) {
      return isSameDepartment;
    } else if (r.role.businessType == null) {
      // default roles with no business to Core
      return business === BUSINESS_VIEW_NAME.CORE && isSameDepartment;
    }

    return false;
  });
  const roleRows = [];
  roleList.forEach((role) => {
    roleRows.push(
      <TableRow key={`${role.role.name}-headcount`}>
        <TableCell width={50}>
          <Row></Row>
        </TableCell>
        <TableCell>
          <Text type={'h100'}>{role.role.label}</Text>
        </TableCell>
        {department.quarters.map((quarter) => {
          const roleHc = speedFilteredHeadcount.find(
            (r) =>
              r.process_path === department.name &&
              r.process_role === role.role.name &&
              r.start_datetime_local === quarter
          );
          return (
            <TableCell key={`${role.role.name}-headcount-${quarter}`}>
              {displayHc(roleHc)}
              {displayThrowRate(roleHc)}
            </TableCell>
          );
        })}
      </TableRow>
    );
  });

  if (roleList.length === 0) {
    return <div></div>;
  }

  return (
    <div className="horizontal-scroll" style={{ backgroundColor: '#fff' }}>
      <Table headerColumns={1} headerRows={1} showDividers={true} showStripes={false} spacing="small">
        <TableRow highlightOnHover={true}>
          <TableCell />
          <TableCell />
          {department['quarters'].map((val, index) => (
            <TableCell key={index}>
              <TimeWindowHeader
                timeline={timeline}
                quarters={department.quarters}
                shift={shift}
                index={index}
                quarter={val}
                speed={speed}
              />
            </TableCell>
          ))}
        </TableRow>
        <TableRow backgroundColor="secondary">
          <TableCell columnSpan={2}>
            <Text>{department.label} Totals</Text>
          </TableCell>
          {department.quarters.map((quarter) => {
            const departmentHc = roleList
              .map((role) =>
                chainWalk(
                  () =>
                    speedFilteredHeadcount.find(
                      (r) =>
                        r.process_path === department.name &&
                        r.process_role === role.role.name &&
                        r.start_datetime_local === quarter
                    ).total_hc,
                  0
                )
              )
              .reduce((departmentHc, roleHc) => departmentHc + roleHc, 0);
            return <TableCell key={`${department.name}-${quarter}`}>{departmentHc}</TableCell>;
          })}
        </TableRow>
        {roleRows}
      </Table>
    </div>
  );
};

const SpeedTable = ({
  speed,
  departments,
  roles,
  headcountAllocation,
  shift,
  timeline,
  singlesEnabled,
  business,
  displayHeader
}) => {
  // Expand 5H (DEFAULT_SPEED) by default, collapse others
  const [isExpanded, setIsExpanded] = useState(speed === DEFAULT_SPEED);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Column spacing="300" width="100%">
      {displayHeader && (
        <TableActionBar
          alignmentHorizontal="justify"
          backgroundColor="primary"
          style={{ cursor: 'pointer' }}
          onClick={toggleExpanded}>
          <Row alignmentVertical="center" spacing="200">
            <Button type="icon" size="small" onClick={toggleExpanded}>
              <Icon tokens={isExpanded ? chevronDownSmallTokens : chevronRightSmallTokens} />
            </Button>
            <Text type="h300" weight="bold">
              {speed} Speed
            </Text>
          </Row>
        </TableActionBar>
      )}
      {(!displayHeader || isExpanded) && (
        <div>
          {departments.map((department) => (
            <DepartmentTable
              key={`${speed}-${department.name}`}
              roles={roles}
              timeline={timeline}
              headcountAllocation={headcountAllocation}
              department={department}
              shift={shift}
              singlesEnabled={singlesEnabled}
              business={business}
              speed={speed}
            />
          ))}
        </div>
      )}
    </Column>
  );
};

const ShiftHeadcountRecommendationTable = ({
  shift,
  departments,
  roles,
  headcountAllocation,
  timeline,
  singlesEnabled,
  business,
  plan
}) => {
  const isRushIndirectRulesEnabled = isFeatureFlagEnabled(
    plan.input.adminSettings,
    SSD_FEATURE_FLAGS_LIST.rushIndirectRulesEnabled
  );

  const speeds = isRushIndirectRulesEnabled ? ALL_SPEEDS : [DEFAULT_SPEED];

  return (
    <Box type={'outline'}>
      <Column spacing="400" width="100%">
        <TableActionBar alignmentHorizontal="justify">
          <Text type="h300">Recommendation</Text>
        </TableActionBar>
        {speeds.map((speed) => (
          <SpeedTable
            key={speed}
            speed={speed}
            roles={roles}
            timeline={timeline}
            headcountAllocation={headcountAllocation}
            departments={departments}
            shift={shift}
            singlesEnabled={singlesEnabled}
            business={business}
            displayHeader={isRushIndirectRulesEnabled}
          />
        ))}
      </Column>
    </Box>
  );
};

export default ShiftHeadcountRecommendationTable;
